import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown, ArrowUp, ArrowUpDown, Ban as Bandage } from 'lucide-react';
import { translate } from '../../lib/translations';
import { InjuryDescriptionModal } from '../Modals/InjuryDescriptionModal';

const formatShortName = (fullName: string) => {
  const normalized = (fullName || '').trim().replace(/\s+/g, ' ');
  if (!normalized) return '';
  const parts = normalized.split(' ');
  if (parts.length < 2) return normalized;
  const firstInitial = parts[0]?.[0]?.toUpperCase() || '';
  const lastName = parts[parts.length - 1] || '';
  if (!firstInitial || !lastName) return normalized;
  return `${firstInitial}.${lastName}`;
};

interface Player {
  playerid: number;
  skaterfullname: string;
  positioncode: string;
  teamabbrevs: string;
  salary: number;
  yearleft: string;
  gamesplayed: number;
  goals: number;
  assists: number;
  points: number;
  plusminus: number;
  pppoints: number;
  shpoints: number;
  gamewinninggoals: number;
  penaltyminutes: number;
  isnhl: boolean;
}

interface OtherTeamsTableProps {
  players: Player[];
  language: 'en' | 'fr';
  injuredPlayers?: Array<{playerid: number, description: string}>;
  highlightPlayerId?: number;
  highlightTerm?: string;
  onViewDetails?: (player: Player) => void;
  // Watchlist props
  watchlistPlayerIds?: Set<number>;
  onAddToWatchlist?: (playerId: number) => void;
  onRemoveFromWatchlist?: (playerId: number) => void;
  watchlistActionInProgress?: boolean;
}

interface Column {
  field: keyof Player;
  translationKey: string;
  sortable?: boolean;
  getValue?: (player: Player) => number | string;
}

interface SortConfig {
  field: keyof Player;
  direction: 'asc' | 'desc';
}

const columns: Column[] = [
  { field: 'skaterfullname', translationKey: 'name', sortable: true },
  { field: 'salary', translationKey: 'salary', sortable: true },
  { field: 'gamesplayed', translationKey: 'gp', sortable: true },
  { field: 'goals', translationKey: 'goals', sortable: true },
  { field: 'assists', translationKey: 'assists', sortable: true },
  { field: 'points', translationKey: 'points', sortable: true },
  {
    field: 'points',
    translationKey: 'ppg',
    sortable: true,
    getValue: (player) =>
      player.gamesplayed > 0
        ? Number((player.points / player.gamesplayed).toFixed(2))
        : 0,
  },
  { field: 'plusminus', translationKey: 'plus.minus', sortable: true },
  { field: 'pppoints', translationKey: 'ppp', sortable: true },
  { field: 'shpoints', translationKey: 'shp', sortable: true },
  { field: 'gamewinninggoals', translationKey: 'gwg', sortable: true },
  { field: 'penaltyminutes', translationKey: 'pim', sortable: true },
];

export function OtherTeamsTable({
  players,
  language,
  injuredPlayers = [],
  highlightPlayerId,
  highlightTerm = '',
  onViewDetails,
  watchlistPlayerIds = new Set(),
  onAddToWatchlist = () => {},
  onRemoveFromWatchlist = () => {},
  watchlistActionInProgress = false
}: OtherTeamsTableProps) {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'points',
    direction: 'desc',
  });
  const [imageErrors, setImageErrors] = useState<{[key: number]: boolean}>({});
  const [showInjuryModal, setShowInjuryModal] = useState(false);
  const [selectedInjury, setSelectedInjury] = useState<{ playerId: number, playerName: string, description: string } | null>(null);
  const highlightRowDesktopRef = useRef<HTMLTableRowElement | null>(null);
  const highlightRowMobileRef = useRef<HTMLTableRowElement | null>(null);

  const [openActionsForId, setOpenActionsForId] = useState<number | null>(null);
  const actionsToggleRef = useRef<HTMLElement | null>(null);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const [actionsMenuStyle, setActionsMenuStyle] = useState<React.CSSProperties>({});

  // Watchlist handlers
  const handleAddToWatchlistClick = (playerId: number) => {
    onAddToWatchlist(playerId);
  };

  const handleRemoveFromWatchlistClick = (playerId: number) => {
    onRemoveFromWatchlist(playerId);
  };

  const isInWatchlist = (playerId: number) => {
    return watchlistPlayerIds.has(playerId);
  };

  const closeActionsMenu = () => {
    setOpenActionsForId(null);
    actionsToggleRef.current = null;
  };

  const updateActionsMenuPosition = () => {
    const toggleEl = actionsToggleRef.current;
    const menuEl = actionsMenuRef.current;
    if (!toggleEl || !menuEl) return;

    const rect = toggleEl.getBoundingClientRect();
    const padding = 8;

    const menuWidth = Math.max(220, menuEl.offsetWidth || 0);
    const naturalMenuHeight = menuEl.offsetHeight || 0;

    const spaceBelow = window.innerHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;
    const openUp = spaceBelow < naturalMenuHeight && spaceAbove > spaceBelow;

    const available = Math.max(140, openUp ? spaceAbove : spaceBelow);
    const maxHeight = Math.min(available, window.innerHeight - padding * 2);
    const visibleHeight = Math.min(naturalMenuHeight || maxHeight, maxHeight);

    let top = openUp ? rect.top - visibleHeight : rect.bottom;
    if (top < padding) top = padding;
    if (top + visibleHeight > window.innerHeight - padding) {
      top = window.innerHeight - padding - visibleHeight;
    }

    let left = rect.left;
    if (left + menuWidth > window.innerWidth - padding) {
      left = window.innerWidth - padding - menuWidth;
    }
    if (left < padding) left = padding;

    setActionsMenuStyle({
      position: 'fixed',
      top,
      left,
      zIndex: 3000,
      maxHeight,
      overflowY: 'auto',
      minWidth: 220,
    });
  };

  useLayoutEffect(() => {
    if (openActionsForId === null) return;
    const raf = requestAnimationFrame(() => {
      updateActionsMenuPosition();
    });
    return () => cancelAnimationFrame(raf);
  }, [openActionsForId]);

  useEffect(() => {
    if (openActionsForId === null) return;

    const onDocPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (actionsMenuRef.current?.contains(target)) return;
      if (actionsToggleRef.current?.contains(target)) return;
      closeActionsMenu();
    };

    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeActionsMenu();
    };

    const onScrollOrResize = () => {
      updateActionsMenuPosition();
    };

    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onEscape);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('scroll', onScrollOrResize, true);

    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onEscape);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [openActionsForId]);

  const handleSort = (field: keyof Player) => {
    setSortConfig((currentConfig) => ({
      field,
      direction:
        currentConfig.field === field && currentConfig.direction === 'desc'
          ? 'asc'
          : 'desc',
    }));
  };

  const sortedPlayers = [...players].sort((a, b) => {
    const aValue = sortConfig.field === 'points' && columns.find(col => col.translationKey === 'ppg')?.getValue
      ? columns.find(col => col.translationKey === 'ppg')!.getValue!(a)
      : a[sortConfig.field];
    const bValue = sortConfig.field === 'points' && columns.find(col => col.translationKey === 'ppg')?.getValue
      ? columns.find(col => col.translationKey === 'ppg')!.getValue!(b)
      : b[sortConfig.field];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortConfig.direction === 'asc'
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });

  // Auto-scroll vers le joueur surligné (après calcul sortedPlayers)
  useEffect(() => {
    const desktopEl = highlightRowDesktopRef.current;
    const mobileEl = highlightRowMobileRef.current;
    const targetEl = (mobileEl && mobileEl.offsetParent !== null) ? mobileEl : desktopEl;

    if (targetEl) {
      const t = setTimeout(() => {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return () => clearTimeout(t);
    }
  }, [highlightPlayerId, sortedPlayers.map(p => p.playerid).join(',')]);

  const getCellValue = (player: Player, column: Column) => {
    if (column.getValue) {
      return column.getValue(player);
    }
    if (column.field === 'salary') {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(player.salary);
    }
    return player[column.field];
  };

  const formatCompactSalaryValue = (amount: number) => {
    const abs = Math.abs(amount);
    if (abs >= 1_000_000) {
      const millions = amount / 1_000_000;
      const rounded = Math.round(millions * 10) / 10;
      const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
      return `$${text}M`;
    }
    if (abs >= 1_000) {
      const thousands = Math.round(amount / 1_000);
      return `$${thousands}K`;
    }
    return `$${Math.round(amount)}`;
  };

  const renderSortIcon = (field: keyof Player, sortable?: boolean) => {
    if (!sortable) return null;

    if (sortConfig.field !== field) {
      return <ArrowUpDown size={16} className="ms-1 text-muted opacity-50" />;
    }

    return sortConfig.direction === 'asc' ? (
      <ArrowUp size={16} className="ms-1 text-primary" />
    ) : (
      <ArrowDown size={16} className="ms-1 text-primary" />
    );
  };

  const handleImageError = (playerId: number) => {
    setImageErrors(prev => ({...prev, [playerId]: true}));
  };

  const getPlayerImageUrl = (player: Player) => {
    if (imageErrors[player.playerid]) {
      return "https://assets.nhle.com/mugs/nhl/20242025/EDM/8478402.png";
    }
    return `https://assets.nhle.com/mugs/nhl/20242025/${player.teamabbrevs}/${player.playerid}.png`;
  };

  const isPlayerInjured = (playerId: number) => {
    return injuredPlayers.some(p => p.playerid === playerId);
  };

  const getInjuryDescription = (playerId: number) => {
    const injury = injuredPlayers.find(p => p.playerid === playerId);
    return injury ? injury.description : 'Joueur blessé';
  };

  const handleShowInjuryModal = (player: Player) => {
    setSelectedInjury({
      playerId: player.playerid,
      playerName: player.skaterfullname,
      description: getInjuryDescription(player.playerid)
    });
    setShowInjuryModal(true);
  };

  return (
    <div className="table-responsive otherteams-table-wrapper">
      <table className="table table-hover table-sm align-middle otherteams-table otherteams-players">
        <thead>
          <tr>
            <th className="otherteams-th-actions" aria-label="Actions" title="Actions"></th>
            <th>Photo</th>
            {columns.map((column) => (
              <th
                key={column.translationKey}
                onClick={() => column.sortable && handleSort(column.field)}
                style={{ cursor: column.sortable ? 'pointer' : 'default' }}
                className="user-select-none"
              >
                <div className="d-flex align-items-center">
                  {translate(column.translationKey, language)}
                  {renderSortIcon(column.field, column.sortable)}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {sortedPlayers.map((player) => (
            <React.Fragment key={`${player.playerid}-${player.skaterfullname}`}>
              {/* Desktop/tablet row (regular table) */}
              <tr
                className={`d-none d-md-table-row ${player.playerid === highlightPlayerId ? 'table-warning' : ''}`}
                ref={player.playerid === highlightPlayerId ? (el) => { highlightRowDesktopRef.current = el; } : undefined}
              >
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary otherteams-actions-toggle"
                    aria-haspopup="menu"
                    aria-expanded={openActionsForId === player.playerid}
                    title={language === 'fr' ? 'Actions' : 'Actions'}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      actionsToggleRef.current = e.currentTarget as unknown as HTMLElement;
                      setOpenActionsForId((prev) => (prev === player.playerid ? null : player.playerid));
                    }}
                  >
                    ⋯
                  </button>
                </td>
                <td className="position-relative otherteams-photo-cell">
                  <img 
                    src={getPlayerImageUrl(player)} 
                    alt={player.skaterfullname}
                    className="border otherteams-photo"
                    style={{ cursor: 'pointer' }}
                    onError={() => handleImageError(player.playerid)}
                    onClick={() => onViewDetails?.(player)}
                  />
                  {isPlayerInjured(player.playerid) && (
                    <span 
                      className="position-absolute" 
                      style={{ top: '-3px', right: '-3px', cursor: 'pointer' }}
                      onClick={() => handleShowInjuryModal(player)}
                    >
                      <Bandage size={14} className="text-danger" />
                    </span>
                  )}
                </td>
                {columns.map((column) => {
                if (column.field === 'skaterfullname') {
                  const fullName = player.skaterfullname;
                  const shortName = formatShortName(fullName);
                  const injured = isPlayerInjured(player.playerid);
                  
                  // Highlight search term if present
                  const renderName = (name: string) => {
                    if (highlightTerm && name.toLowerCase().includes(highlightTerm.toLowerCase())) {
                      const regex = new RegExp(`(${highlightTerm})`, 'ig');
                      const parts = name.split(regex);
                      return parts.map((part, idx) => part.toLowerCase() === highlightTerm.toLowerCase() ? (
                        <mark key={idx} className="px-0 bg-warning text-dark">{part}</mark>
                      ) : (
                        <React.Fragment key={idx}>{part}</React.Fragment>
                      ));
                    }
                    return name;
                  };

                  return (
                    <td key={column.translationKey}>
                      <div className="d-flex align-items-center gap-1">
                        <span className="otherteams-player-name-full" style={{ cursor: 'pointer' }} onClick={() => onViewDetails?.(player)}>{renderName(fullName)}</span>
                        <span className="otherteams-player-name-short" style={{ cursor: 'pointer' }} onClick={() => onViewDetails?.(player)}>{renderName(shortName)}</span>
                        {injured && (
                          <Bandage 
                            size={14} 
                            className="text-danger ms-1" 
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleShowInjuryModal(player)}
                          />
                        )}
                      </div>
                    </td>
                  );
                }

                if (column.field === 'salary') {
                  const fullSalary = String(getCellValue(player, column) ?? '');
                  const compactSalary = formatCompactSalaryValue(player.salary);
                  return (
                    <td key={column.translationKey}>
                      <span className="otherteams-salary-full">{fullSalary}</span>
                      <span className="otherteams-salary-compact">{compactSalary}</span>
                    </td>
                  );
                }

                if (column.field === 'points') {
                  const labels =
                    language === 'fr'
                      ? { goals: 'B', assists: 'P', points: 'PTS' }
                      : { goals: 'G', assists: 'A', points: 'PTS' };
                  const compact = `${labels.goals} ${player.goals}  ${labels.assists} ${player.assists}  ${labels.points} ${player.points}`;
                  return (
                    <td key={column.translationKey}>
                      <span className="otherteams-points-full">{player.points}</span>
                      <span className="otherteams-points-compact">{compact}</span>
                    </td>
                  );
                }

                return (
                  <td key={column.translationKey}>
                    <div className="d-flex align-items-center">
                      {getCellValue(player, column)}
                    </div>
                  </td>
                );
                })}
              </tr>

              {/* Mobile row: single horizontally-scrollable strip inside one cell */}
              <tr
                className={`d-table-row d-md-none ${player.playerid === highlightPlayerId ? 'table-warning' : ''}`}
                ref={player.playerid === highlightPlayerId ? (el) => { highlightRowMobileRef.current = el; } : undefined}
              >
                <td colSpan={2 + columns.length}>
                  <div className="otherteams-row-scroll">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary otherteams-actions-toggle"
                      aria-haspopup="menu"
                      aria-expanded={openActionsForId === player.playerid}
                      title={language === 'fr' ? 'Actions' : 'Actions'}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        actionsToggleRef.current = e.currentTarget as unknown as HTMLElement;
                        setOpenActionsForId((prev) => (prev === player.playerid ? null : player.playerid));
                      }}
                    >
                      ⋯
                    </button>

                    <span className="position-relative otherteams-photo-cell">
                      <img
                        src={getPlayerImageUrl(player)}
                        alt={player.skaterfullname}
                        className="border otherteams-photo"
                        style={{ cursor: 'pointer' }}
                        onError={() => handleImageError(player.playerid)}
                        onClick={() => onViewDetails?.(player)}
                      />
                      {isPlayerInjured(player.playerid) && (
                        <span
                          className="position-absolute"
                          style={{ top: '-3px', right: '-3px', cursor: 'pointer' }}
                          onClick={() => handleShowInjuryModal(player)}
                        >
                          <Bandage size={14} className="text-danger" />
                        </span>
                      )}
                    </span>

                    <span className="otherteams-player-name-short" style={{ cursor: 'pointer' }} onClick={() => onViewDetails?.(player)}>{formatShortName(player.skaterfullname)}</span>
                    <span className="otherteams-points-compact">
                      {language === 'fr'
                        ? `B ${player.goals}  P ${player.assists}  PTS ${player.points}`
                        : `G ${player.goals}  A ${player.assists}  PTS ${player.points}`}
                    </span>
                    <span className="otherteams-salary-compact">{formatCompactSalaryValue(player.salary)}</span>
                  </div>
                </td>
              </tr>

              {openActionsForId === player.playerid &&
                createPortal(
                  <div
                    ref={actionsMenuRef}
                    className="dropdown-menu show"
                    style={actionsMenuStyle}
                    role="menu"
                  >
                    {onViewDetails && (
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          onViewDetails(player);
                          closeActionsMenu();
                        }}
                      >
                        {language === 'fr' ? 'Voir les détails' : 'View details'}
                      </button>
                    )}

                    <button
                      type="button"
                      className="dropdown-item"
                      disabled={watchlistActionInProgress}
                      onClick={() => {
                        if (isInWatchlist(player.playerid)) {
                          handleRemoveFromWatchlistClick(player.playerid);
                        } else {
                          handleAddToWatchlistClick(player.playerid);
                        }
                        closeActionsMenu();
                      }}
                    >
                      {language === 'fr'
                        ? isInWatchlist(player.playerid)
                          ? 'Retirer de la liste de surveillance'
                          : 'Ajouter à la liste de surveillance'
                        : isInWatchlist(player.playerid)
                          ? 'Remove from watchlist'
                          : 'Add to watchlist'}
                    </button>

                    {isPlayerInjured(player.playerid) && (
                      <>
                        <hr className="dropdown-divider" />
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={() => {
                            handleShowInjuryModal(player);
                            closeActionsMenu();
                          }}
                        >
                          {language === 'fr' ? 'Voir la blessure' : 'View injury'}
                        </button>
                      </>
                    )}
                  </div>,
                  document.body
                )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      <InjuryDescriptionModal
        show={showInjuryModal}
        onClose={() => setShowInjuryModal(false)}
        playerName={selectedInjury?.playerName || ''}
        description={selectedInjury?.description || ''}
        language={language}
      />
    </div>
  );
}